'use client'

import { useHeaderFooter } from '@cdc-internal/mkt-header-footer'
import { AppShell, AppShellHeader, AppShellMain, Box, Container } from '@mantine/core'
import { useParams } from 'next/navigation'
import { useEffect, useRef } from 'react'

import { fiatCurrencies } from '@/configs/currencies'
import { isProductionAppEnv } from '@/configs/env'
import { useSsrContext } from '@/contexts/ssr'
import { useIsResponsiveMobileView } from '@/hooks/responsive'
import useDir from '@/hooks/useDir'
import { isUSRegion } from '@/i18n/country'
import { useBasePath, useI18nRouter } from '@/i18n/navigation'
import { setSelectedCurrency, setUserLocale } from '@/server/actions'

interface AppLayoutProps {
  locale: string
  children: React.ReactNode
  countryCode: string
}

const PRICE_RESERVED_SEGMENTS = new Set(['categories', 'stocks', 'tokenized-stocks'])

function useHeaderHeightSync(headerRef: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const headerWrapper = headerRef.current
    if (!headerWrapper) return
    const apply = (height: number) => {
      const rootStyle = document.documentElement.style
      rootStyle.setProperty('--app-shell-header-height', `${height}px`)
      rootStyle.setProperty('--app-shell-header-offset', `${height}px`)
    }
    let observedElement: HTMLElement | null = null
    const bind = (element: HTMLElement) => {
      observedElement = element
      resizeObserver.disconnect()
      resizeObserver.observe(element)
    }
    const resizeObserver = new ResizeObserver(() => {
      const realHeaderElement = headerWrapper.getElementsByTagName('header')[0]
      if (!realHeaderElement || !realHeaderElement.isConnected) return
      if (realHeaderElement !== observedElement) bind(realHeaderElement)
      apply(realHeaderElement.clientHeight)
    })
    const realHeaderElement = headerWrapper.getElementsByTagName('header')[0]
    if (realHeaderElement) {
      bind(realHeaderElement)
      apply(realHeaderElement.clientHeight)
    }
    return () => {
      resizeObserver.disconnect()
      document.documentElement.style.removeProperty('--app-shell-header-height')
      document.documentElement.style.removeProperty('--app-shell-header-offset')
    }
  }, [headerRef])
}

export default function AppLayout({ children, locale, countryCode }: AppLayoutProps) {
  const { selectedCurrency } = useSsrContext()
  const basePath = useBasePath()
  const { locale: pathLocale } = useParams<{ locale: string }>()
  const is18nRouter = useI18nRouter()
  const isResponsiveMobileView = useIsResponsiveMobileView()
  const headerRef = useRef<HTMLDivElement>(null)
  useHeaderHeightSync(headerRef)

  const pathParts = basePath.split('/').filter(Boolean)
  const isPriceSection = pathParts[0] === 'price'

  const isCryptoDetailPage =
    isPriceSection && pathParts.length === 2 && !PRICE_RESERVED_SEGMENTS.has(pathParts[1])
  const isStockDetailPage = isPriceSection && pathParts.length >= 3 && pathParts[1] === 'stocks'
  const isCategoryDetailPage =
    isPriceSection &&
    pathParts.length === 3 &&
    pathParts[1] === 'categories' &&
    pathParts[2] !== 'tokenized-stocks'
  const isUSHomePage = pathParts.length === 1 && isUSRegion(pathLocale)
  const useHomeMode =
    isUSHomePage ||
    (isUSRegion(pathLocale) && (isCryptoDetailPage || isStockDetailPage || isCategoryDetailPage))
  const isCurrencyPage = pathParts.length >= 3
  const pathCurrency = isCurrencyPage ? pathParts[2].toUpperCase() : null
  const isValidPathCurrency = pathCurrency && fiatCurrencies.some(c => c.code === pathCurrency)
  const displayCurrency = isValidPathCurrency ? pathCurrency : selectedCurrency

  const { Header, Footer } = useHeaderFooter({
    environment: isProductionAppEnv ? 'production' : 'staging',
    isMobile: isResponsiveMobileView,
    countryCode,
    locale: pathLocale,
    language: locale,
    currency: displayCurrency,
    onClickCurrency: async currency => {
      await setSelectedCurrency(currency)
      if (isValidPathCurrency) {
        is18nRouter.replace(`/${pathParts[1]}/${currency.toLowerCase()}`)
      }
    },
    isAuthCheckRequired: false,
    onClickLanguageRegion: async newLocale => {
      is18nRouter.replace(basePath, { locale: newLocale })

      await setUserLocale(newLocale)
    },
    source: 'cdc-price',
    mode: useHomeMode ? 'home' : undefined,
  })
  const dir = useDir()
  return (
    <AppShell header={{ height: 54 }}>
      <AppShellHeader dir={dir} ref={headerRef}>
        <Header />
      </AppShellHeader>

      <AppShellMain>
        {/* Main Content Area */}
        <Container
          size={1312}
          px="sm"
          pt="40px"
          // Minimum height of the main content area will fill the viewport height except the header
          mih="calc(100dvh - var(--app-shell-header-height))"
          display={'flex'}
          style={{ flexDirection: 'column' }}
        >
          {children}
        </Container>

        <Box dir={dir}>
          <Footer />
        </Box>
      </AppShellMain>
    </AppShell>
  )
}
